"use client";

import type React from "react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Textarea,
  VStack,
  Accordion,
  useToast,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { ArrowLeft, Save, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { RetellWebClient } from "retell-client-js-sdk";
import {
  agentService,
  type Agent,
  type RetellLLM,
  type VoiceDetails,
  type UpdateRetellLLMParams,
} from "../../voice-services/agentService";
import { ModelSelect } from "./ModelSelect";
import { VoiceSelect } from "./VoiceSelect";
import { LanguageSelect } from "./LanguageSelect";
import { WelcomeMessageSelect } from "./WelcomeMessageSelect";
import { callService } from "../../voice-services/callService";
import { WebhookSettings } from "./WebhookSettings";
import { SecuritySettings } from "./SecuritySettings";
import { CallSettings } from "./call-settings/CallSettings";
import { PostCallAnalysis } from "./post-call-analysis/PostCallAnalysis";
import { SpeechSettings } from "./speech-settings/SpeechSettings";
import { AgentKnowledgeBase } from "./knowledge-base/AgentKnowledgeBase";
import { AgentFunctionsWrapper } from "./functions/AgentFunctionsWrapper";
import { DynamicVariablesButton } from "./DynamicVariablesButton";
import { EditableAgentName } from "./EditableAgentName";

const waveDraw = keyframes`
  /* ====== DRAW IN from center ====== */
  0% {
    opacity: 0;
    stroke-dasharray: 0 100;
    stroke-dashoffset: 50;
    transform: scale(0.3);  /* start smaller */
  }
  12% { opacity: 0.4; }

  /* fully drawn by ~40% */
  40% {
    opacity: 0.45;
    stroke-dasharray: 100 0;
    stroke-dashoffset: 50;
    transform: scale(1.25);
  }

  /* ====== HOLD + GROW ~0.5s ====== */
  58% {
    opacity: 0.4;
    stroke-dasharray: 100 0;
    stroke-dashoffset: 50;
    transform: scale(1.4);  /* spread wider */
  }

  /* ====== FADE OUT ====== */
  68% {
    opacity: 0;
    stroke-dasharray: 100 0;
    stroke-dashoffset: 50;
    transform: scale(1.5);
  }

  /* ====== GAP (nothing visible) ====== */
  100% {
    opacity: 0;
    stroke-dasharray: 100 0;
    stroke-dashoffset: 50;
    transform: scale(1.5);
  }
`;

interface AgentConfigProps {
  agent: Agent;
}

function getShortId(id: string) {
  if (!id) return "";
  const len = id.length;
  return len > 5 ? `${id.slice(0, 2)}...${id.slice(-3)}` : id;
}

export const AgentConfig: React.FC<AgentConfigProps> = ({
  agent: initialAgent,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin } = useAuth();
  const retellWebClientRef = useRef<RetellWebClient | null>(null);

  const [agent, setAgent] = useState<Agent>(initialAgent);
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [noteVisible, setNoteVisible] = useState(false);
  const [voices, setVoices] = useState<VoiceDetails[]>([]);
  const [selectedLLM, setSelectedLLM] = useState<RetellLLM | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<VoiceDetails | null>(null);
  const [isCallStarting, setIsCallStarting] = useState(false);
  const [callData, setCallData] = useState<{
    accessToken: string;
    callId: string;
  } | null>(null);

  const [isCallActive, setIsCallActive] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});

  // Minimal, consistent scrollbar styling (theme-friendly)
  const scrollbarStyles = {
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "gray.300",
      borderRadius: "6px",
      border: "2px solid transparent",
    },
  };

  const llmId =
    agent.response_engine?.type === "retell-llm"
      ? agent.response_engine.llm_id
      : null;

  const isRetellLLMAgent = llmId !== null;

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const voicesResponse = await agentService.listVoices();
        setVoices(voicesResponse);

        // Only load Retell LLM data if agent uses retell-llm
        if (isRetellLLMAgent && llmId) {
          const currentLLM = await agentService.getRetellLLM(llmId);
          setSelectedLLM(currentLLM);
          setPrompt(currentLLM.general_prompt || "");
        } else {
          setSelectedLLM(null);
          setPrompt("");
        }

        const foundVoice = voicesResponse.find(
          (v) => v.voice_id === agent.voice_id,
        );
        if (foundVoice) setSelectedVoice(foundVoice);
      } catch (error: any) {
        console.error("Error loading data:", error);
        toast({ title: "Error loading data", status: "error", duration: 3000 });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
    // NOTE: we intentionally depend on llmId + isRetellLLMAgent, not the entire agent object
  }, [agent.voice_id, llmId, isRetellLLMAgent, toast]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSave = async () => {
    if (!selectedLLM) return;
    setIsSaving(true);
    try {
      const params: UpdateRetellLLMParams = { general_prompt: prompt };
      const updated = await agentService.updateRetellLLM(
        selectedLLM.llm_id,
        params,
      );
      setSelectedLLM(updated);
      toast({
        title: "Prompt saved successfully",
        status: "success",
        duration: 3000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error saving prompt",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const memoizedWelcomeMessageSelect = useMemo(() => {
    if (!selectedLLM) return null;
    return (
      <WelcomeMessageSelect
        initialLLM={selectedLLM}
        agentId={agent.agent_id}
        onLLMUpdate={(updatedLLM: RetellLLM) => setSelectedLLM(updatedLLM)}
      />
    );
  }, [selectedLLM, agent.agent_id]);

  const initializeRetellWebClient = useCallback(() => {
    if (!retellWebClientRef.current) {
      retellWebClientRef.current = new RetellWebClient();
      retellWebClientRef.current.on("call_started", () =>
        setIsCallActive(true),
      );
      retellWebClientRef.current.on("call_ended", () => {
        setIsCallActive(false);
        setCallData(null);
      });
      retellWebClientRef.current.on("error", (error: any) => {
        console.error("An error occurred:", error);
        toast({
          title: "Call Error",
          description:
            error.response?.data?.message ||
            "An error occurred during the call",
          status: "error",
          duration: 5000,
        });
        retellWebClientRef.current?.stopCall();
      });
    }
  }, [toast]);

  const joinCall = useCallback(
    async (accessToken: string) => {
      if (!retellWebClientRef.current) initializeRetellWebClient();
      try {
        await retellWebClientRef.current?.startCall({
          accessToken,
          sampleRate: 24000,
        });
        toast({
          title: "Joined call successfully",
          status: "success",
          duration: 3000,
        });
      } catch (error: any) {
        console.error("Error joining call:", error);
        toast({
          title: "Error joining call",
          description: error?.message || "Unknown error",
          status: "error",
          duration: 5000,
        });
      }
    },
    [initializeRetellWebClient, toast],
  );

  const handleStartCall = useCallback(async () => {
    setIsCallStarting(true);
    try {
      const callOptions: any = {};
      if (Object.keys(dynamicVariables).length > 0) {
        callOptions.retell_llm_dynamic_variables = dynamicVariables;
      }
      const response = await callService.createWebCall(
        agent.agent_id,
        callOptions,
      );
      const newCallData = {
        accessToken: response.access_token,
        callId: response.call_id,
      };
      setCallData(newCallData);
      toast({
        title: "Web call created",
        description: `Call ID: ${response.call_id}`,
        status: "success",
        duration: 5000,
      });
      await joinCall(response.access_token);
    } catch (error: any) {
      console.error("Error starting web call:", error);
      toast({
        title: "Error starting web call",
        description: error?.message || "Unknown error",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsCallStarting(false);
    }
  }, [agent.agent_id, toast, joinCall, dynamicVariables]);

  const handleEndCall = useCallback(() => {
    retellWebClientRef.current?.stopCall();
    setIsCallActive(false);
    setCallData(null);
  }, []);

  const handleDynamicVariablesChange = useCallback(
    (vars: Record<string, string>) => {
      if (Object.keys(vars).length > 0) console.log("Variables updated:", vars);
      setDynamicVariables(vars);
    },
    [],
  );

  const handleVoiceChangeFromModel = useCallback(
    async (voiceId: string) => {
      const voice = voices.find((v) => v.voice_id === voiceId);
      if (!voice) throw new Error("Voice not found");

      await agentService.update(agent.agent_id, {
        voice_id: voiceId,
        voice_model: null,
      });

      setSelectedVoice(voice);
    },
    [voices, agent.agent_id],
  );

  const testPanel = useMemo(
    () => (
      <VStack
        w="250px"
        p={4}
        borderLeft="1px solid"
        borderColor="gray.200"
        spacing={4}
      >
        <Flex align="center" justify="space-between" w="full" bg="gray.100">
          <Text px={3} py={1} rounded="md" fontWeight="semibold">
            Test your agent
          </Text>
          <DynamicVariablesButton
            onVariablesChange={handleDynamicVariablesChange}
            prompt={prompt}
            beginMessage={selectedLLM?.begin_message || ""}
          />
        </Flex>

        <Divider />

        <Box textAlign="center" py={8}>
          <Box
            role="group"
            display="inline-block"
            position="relative"
            w="110px"
            h="110px"
            cursor="pointer"
          >
            {/* Mic emoji */}
            <Box
              position="absolute"
              left="50%"
              top="50%"
              transform="translate(-50%, -50%)"
              fontSize="48px"
              color="primary.500"
              zIndex={2}
              _groupHover={{
                transform: "translate(-50%, -50%) scale(1.05)",
                transition: "transform 0.15s ease",
                filter: "drop-shadow(0 0 6px rgba(13,217,255,0.9))",
              }}
            >
              🎙️
            </Box>

            {/* Arcs layer */}
            <Box
              position="absolute"
              inset="0"
              color="primary.500"
              pointerEvents="none"
              sx={{
                "& .arc": {
                  transformOrigin: "50% 50%",
                  transformBox: "fill-box",
                  opacity: 0,
                  strokeDasharray: "0 100",
                  strokeDashoffset: 50,
                  willChange: "transform, opacity, stroke-dasharray",
                },

                // one unified wave animation for all arcs
                "[role=group]:hover & .arc": {
                  animation: `${waveDraw} 2.7s ease-out infinite`,
                },

                // stagger the pulses
                "[role=group]:hover & .wave1": { animationDelay: "0s" },
                "[role=group]:hover & .wave2": { animationDelay: "0.28s" },
                "[role=group]:hover & .wave3": { animationDelay: "0.56s" },
              }}
            >
              <svg width="100%" height="100%" viewBox="0 0 110 110">
                {/* ================= RIGHT WAVES (sound-wave style) ================= */}
                <path
                  className="arc right wave1"
                  pathLength={100}
                  d="M72 30 Q92 55 72 80"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  className="arc right wave2"
                  pathLength={100}
                  d="M79 24 Q102 55 79 86"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
                <path
                  className="arc right wave3"
                  pathLength={100}
                  d="M86 18 Q108 55 86 92"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />

                {/* ================= LEFT WAVES (mirror of right) ================= */}
                <path
                  className="arc left wave1"
                  pathLength={100}
                  d="M38 30 Q18 55 38 80"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  className="arc left wave2"
                  pathLength={100}
                  d="M31 24 Q8 55 31 86"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
                <path
                  className="arc left wave3"
                  pathLength={100}
                  d="M24 18 Q2 55 24 92"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </Box>
          </Box>

          <Button
            mt={4}
            w="full"
            onClick={isCallActive ? handleEndCall : handleStartCall}
            isLoading={isCallStarting}
            loadingText="Starting call..."
            colorScheme="primary"
            bg="primary.600"
            _hover={{ bg: "primary.700" }}
            variant="solid"
          >
            {isCallActive ? "End Call" : "Start Call"}
          </Button>
        </Box>
      </VStack>
    ),
    [
      isCallActive,
      handleEndCall,
      handleStartCall,
      isCallStarting,
      handleDynamicVariablesChange,
      prompt,
      selectedLLM?.begin_message,
    ],
  );

  const mainContent = useMemo(
    () => (
      <Flex h="100vh" bg="white" color="gray.800" direction="column">
        {/* Header */}
        <Flex
          px={4}
          py={3}
          borderBottom="1px solid"
          borderColor="gray.200"
          align="center"
        >
          <IconButton
            aria-label="Back"
            icon={<ArrowLeft />}
            variant="ghost"
            color="gray.800"
            _hover={{ bg: "gray.100" }}
            onClick={() => navigate("/voice-agents")}
          />
          <Box ml={4}>
            <EditableAgentName
              agentId={agent.agent_id}
              initialName={agent.agent_name || ""}
            />
            <Text color="gray.500" fontSize="sm">
              Single prompt &bull; Agent ID: {getShortId(agent.agent_id)}{" "}
              <IconButton
                aria-label="Copy Agent ID"
                icon={<Copy size={12} />}
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(agent.agent_id)}
                color="gray.500"
              />
              {isRetellLLMAgent && llmId ? (
                <>
                  &nbsp;&bull;&nbsp;LLM ID: {getShortId(llmId)}{" "}
                  <IconButton
                    aria-label="Copy LLM ID"
                    icon={<Copy size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => copyToClipboard(llmId)}
                    color="gray.500"
                  />
                </>
              ) : (
                <>
                  &nbsp;&bull;&nbsp;LLM:{" "}
                  {agent.response_engine.type === "custom-llm"
                    ? "Custom LLM"
                    : "N/A"}
                </>
              )}
            </Text>
          </Box>
        </Flex>

        {/* Toolbar */}
        <Flex
          px={4}
          py={2}
          borderBottom="1px solid"
          borderColor="gray.200"
          gap={2}
        >
          <ModelSelect
            initialLLM={selectedLLM}
            agentId={agent.agent_id}
            setSelectedLLM={setSelectedLLM}
            currentVoice={selectedVoice}
            onVoiceChange={handleVoiceChangeFromModel}
          />
          <VoiceSelect
            initialVoice={selectedVoice}
            voices={voices}
            agentId={agent.agent_id}
            setSelectedVoice={setSelectedVoice}
            currentModel={selectedLLM}
          />
          <LanguageSelect
            initialLanguage={agent.language}
            agentId={agent.agent_id}
          />
        </Flex>

        {/* Body */}
        <Flex flex={1} overflow="hidden" alignItems="stretch">
          {/* Prompt editor */}
          <Box flex={1} p={4} borderRight="1px solid" borderColor="gray.200">
            <VStack
              align="stretch"
              h="full"
              spacing={4}
              overflowY="auto"
              css={scrollbarStyles}
            >
              <Box position="relative" flex={0} minH="400px">
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                  }}
                  resize="none"
                  fontSize="sm"
                  placeholder="Type in a universal prompt for your agent, such as its role, conversational style, objective, etc."
                  pr="100px"
                  height="100%"
                  onMouseEnter={() => setNoteVisible(true)}
                  onMouseLeave={() => setNoteVisible(false)}
                  isDisabled={!isRetellLLMAgent}
                  isReadOnly={!isAdmin}
                />
                {!isAdmin && (
                  <Text fontSize="xs" color="orange.400" mt={1}>Prompt editing is restricted to administrators.</Text>
                )}
                <Button
                  leftIcon={<Save size={16} />}
                  onClick={handleSave}
                  isLoading={isSaving}
                  position="absolute"
                  bottom="8px"
                  right="8px"
                  size="sm"
                  colorScheme="primary"
                  bg="primary.600"
                  _hover={{ bg: "primary.700" }}
                  zIndex={2}
                  isDisabled={!isRetellLLMAgent || !selectedLLM}
                >
                  Save
                </Button>
                {noteVisible && (
                  <Text
                    position="absolute"
                    bottom="8px"
                    left="8px"
                    fontSize="xs"
                    color="gray.500"
                  >
                    Use `{"{{_}}"}` to add variables.
                  </Text>
                )}
              </Box>
              {memoizedWelcomeMessageSelect}
            </VStack>
          </Box>

          {/* Right rail */}
          <VStack
            w="300px"
            spacing={0}
            overflow="auto"
            p={4}
            borderRight="1px solid"
            borderColor="gray.200"
            css={scrollbarStyles}
          >
            <Accordion allowMultiple w="full">
              {isRetellLLMAgent && llmId ? (
                <>
                  {isAdmin && (
                    <AgentFunctionsWrapper
                      agentId={agent.agent_id}
                      llmId={llmId}
                      initialFunctions={selectedLLM?.general_tools || []}
                    />
                  )}
                  <AgentKnowledgeBase
                    agentId={agent.agent_id}
                    llmId={llmId}
                    initialLLM={selectedLLM}
                    onLLMUpdate={setSelectedLLM}
                  />
                </>
              ) : null}

              <SpeechSettings agentId={agent.agent_id} />
              <CallSettings
                agentId={agent.agent_id}
                initialSettings={{
                  enable_voicemail_detection: agent.enable_voicemail_detection,
                  end_call_after_silence_ms: agent.end_call_after_silence_ms,
                  max_call_duration_ms: agent.max_call_duration_ms,
                  begin_message_delay_ms: agent.begin_message_delay_ms,
                }}
              />
              {isAdmin && (
                <PostCallAnalysis
                  agentId={agent.agent_id}
                  initialAnalysisData={agent.post_call_analysis_data ?? null}
                />
              )}
              {isAdmin && (
                <SecuritySettings
                  agentId={agent.agent_id}
                  initialOptOut={
                    agent.data_storage_setting === "basic_attributes_only"
                  }
                  initialFallbackVoiceIds={agent.fallback_voice_ids || null}
                />
              )}
              {isAdmin && (
                <WebhookSettings
                  agentId={agent.agent_id}
                  initialWebhookUrl={agent.agent_webhook_url}
                />
              )}
            </Accordion>
          </VStack>

          {/* Test Panel */}
          {testPanel}
        </Flex>
      </Flex>
    ),
    [
      agent,
      prompt,
      voices,
      selectedLLM,
      selectedVoice,
      noteVisible,
      memoizedWelcomeMessageSelect,
      navigate,
      testPanel,
      isSaving,
      handleVoiceChangeFromModel,
      isRetellLLMAgent,
      llmId,
    ],
  );

  return isLoading ? (
    <Flex h="100vh" justify="center" align="center" bg="white">
      <Spinner size="xl" color="primary.500" />
    </Flex>
  ) : (
    mainContent
  );
};
